/**
 * LegalTemplates PDF Editor — text fragments, inline and block text editing
 *
 * Split from the former public/js/pdf-editor.js IIFE (Phase 1, Sep 2026).
 * Page config is injected by the Blade view as window.pdfEditorConfig; pdf.js
 * and pdf-lib are bundled from npm through context.js (no CDN).
 */
import { pdfjsLib } from './context.js';
import {
    escapeSvg,
    getCommentAnnotationBounds,
    pushHistory,
    redrawPage,
    setSelectedAnnotation,
    syncStyleControlsFromAnnotation,
} from './annotations.js';
import {
    BLOCK_DEFAULT_ASCENT,
    BLOCK_DEFAULT_DESCENT,
    saveButton,
    state,
    textAlignEl,
    textBoldEl,
    textFontEl,
    textInputsEl,
    textItalicEl,
    textUnderlineEl,
} from './context.js';
import {
    buildPageTextLines,
    clamp,
    cssColorToHex,
    finiteNumber,
    generateId,
    normalizeBackgroundColor,
    normalizeInkColor,
    round1,
    sampleCanvasBackgroundColor,
    sampleCanvasInkColor,
} from './core.js';
import { commitInlineFormFieldEditor } from './form-fill.js';
import { textFragmentsCanMerge } from './modals.js';
import { getPageShellRect } from './shell.js';
import { getToolColor, getToolSize, setStatus } from './ui.js';

export function commitAllInlineEditors(saveText = true) {
    commitInlineTextEditor(saveText);
    commitInlineCommentEditor(saveText);
    commitInlineReplaceEditor(saveText);
    commitInlineFormFieldEditor(saveText);
    commitBlockTextEditor(saveText);
}

export function fontFamilyToCss(fontFamily) {
    if (fontFamily === 'TimesRoman') {
        return "'Times New Roman', Times, serif";
    }

    if (fontFamily === 'Courier') {
        return "'Courier New', Courier, monospace";
    }

    return "Helvetica, Arial, sans-serif";
}

export function getSelectedTextStyle() {
    return {
        fontFamily: state.textStyle.fontFamily,
        bold: state.textStyle.bold,
        italic: state.textStyle.italic,
        underline: state.textStyle.underline,
        align: state.textStyle.align,
        fontSize: Math.max(10, getToolSize()),
        color: getToolColor(),
    };
}

export function resolveStandardFontName(style) {
    const family = style.fontFamily || 'Helvetica';
    const bold = !!style.bold;
    const italic = !!style.italic;

    if (family === 'TimesRoman') {
        if (bold && italic) return 'TimesRomanBoldItalic';
        if (bold) return 'TimesRomanBold';
        if (italic) return 'TimesRomanItalic';
        return 'TimesRoman';
    }

    if (family === 'Courier') {
        if (bold && italic) return 'CourierBoldOblique';
        if (bold) return 'CourierBold';
        if (italic) return 'CourierOblique';
        return 'Courier';
    }

    if (bold && italic) return 'HelveticaBoldOblique';
    if (bold) return 'HelveticaBold';
    if (italic) return 'HelveticaOblique';
    return 'Helvetica';
}

// Resolve the true font style for a text item. getTextContent() only gives
// internal names like "g_d2_f1", but after the page renders, pdf.js's
// commonObjs holds the real font ("Times-Bold", bold/italic flags). Fall
// back to the style entry's generic CSS family ("serif" -> Times).
export function resolvePdfFontStyle(pdfPage, fontName, styleEntry = {}) {
    let fontObj = null;
    try {
        if (fontName && pdfPage && pdfPage.commonObjs && pdfPage.commonObjs.has(fontName)) {
            fontObj = pdfPage.commonObjs.get(fontName);
        }
    } catch (error) {
        fontObj = null;
    }

    const mapped = mapPdfFontNameToStyle(fontObj && fontObj.name ? fontObj.name : fontName);
    if (mapped.fontFamily === 'Helvetica') {
        const cssFamily = String(styleEntry.fontFamily || '').toLowerCase();
        if (cssFamily.includes('mono') || cssFamily.includes('courier')) {
            mapped.fontFamily = 'Courier';
        } else if (cssFamily.includes('serif') && !cssFamily.includes('sans')) {
            mapped.fontFamily = 'TimesRoman';
        }
    }

    if (fontObj) {
        mapped.bold = mapped.bold || !!fontObj.bold || !!fontObj.black;
        mapped.italic = mapped.italic || !!fontObj.italic;
    }

    return mapped;
}

export function mapPdfFontNameToStyle(fontName) {
    const normalized = String(fontName || '').toLowerCase();
    const isCourier = normalized.includes('courier');
    const isTimes = normalized.includes('times');

    return {
        fontFamily: isCourier ? 'Courier' : (isTimes ? 'TimesRoman' : 'Helvetica'),
        bold: /(bold|black|demi|semibold)/.test(normalized),
        italic: /(italic|oblique)/.test(normalized),
    };
}

export function enrichTextFragmentsFromCanvas(fragments, context, canvas, dpr = 1) {
    if (!Array.isArray(fragments) || !fragments.length) {
        return fragments;
    }

    fragments.forEach((fragment) => {
        const sampledColor = sampleCanvasInkColor(context, canvas, fragment, dpr);
        if (sampledColor) {
            fragment.color = sampledColor;
        }

        fragment.backgroundColor = sampleCanvasBackgroundColor(context, canvas, fragment, dpr);
    });

    return fragments;
}

export async function extractPageTextFragments(pdfPage, viewport, scale, pageNumber) {
    let textContent;
    try {
        // pdf.js 5: items are combined by default; keep the raw whitespace so
        // fragment widths match what was drawn.
        textContent = await pdfPage.getTextContent({
            disableNormalization: true,
        });
    } catch (error) {
        return [];
    }

    if (!textContent || !Array.isArray(textContent.items)) {
        return [];
    }

    const fragments = [];
    const pageWidth = Math.max(1, viewport.width);
    const pageHeight = Math.max(1, viewport.height);
    const styles = textContent.styles || {};

    textContent.items.forEach((item, index) => {
        const text = String(item && item.str ? item.str : '');
        if (!text.trim()) {
            return;
        }

        if (!Array.isArray(item.transform) || item.transform.length < 6) {
            return;
        }

        const tx = pdfjsLib.Util.transform(viewport.transform, item.transform);
        const angle = Math.atan2(tx[1], tx[0]);
        if (Math.abs(angle) > 0.15) {
            return;
        }

        const trueGlyphHeight = Math.hypot(tx[2], tx[3]);
        const glyphHeight = Math.max(8, trueGlyphHeight);
        const styleEntry = item.fontName && styles[item.fontName] ? styles[item.fontName] : {};
        const rawAscent = Number(styleEntry.ascent);
        const rawDescent = Number(styleEntry.descent);
        const ascentRatio = Number.isFinite(rawAscent) && rawAscent > 0 ? clamp(rawAscent, 0.55, 1.15) : 0.86;
        const descentRatio = Number.isFinite(rawDescent) ? clamp(Math.abs(rawDescent), 0.04, 0.45) : 0.14;
        const verticalRatio = clamp(ascentRatio + descentRatio, 0.86, 1.35);
        const scaledHeight = Math.abs(Number(item.height || 0) * scale);
        const height = Math.max(glyphHeight * verticalRatio, scaledHeight || 0);
        const scaledWidth = Math.abs(Number(item.width || 0) * scale);
        // The estimate is for items pdf.js gives no width for. Its per-glyph
        // floor is meant for legible 8px text, so on a small render it made
        // every fragment several times wider than its ink and merged the
        // columns of a form; a measured width always wins.
        const fallbackWidth = Math.max(height * 0.42, text.length * Math.max(5, glyphHeight * 0.46));
        const width = scaledWidth > 0 ? scaledWidth : fallbackWidth;

        const rawX = Number(tx[4] || 0);
        const baselineY = clamp(Number(tx[5] || 0), 0, pageHeight);
        const rawY = baselineY - (glyphHeight * ascentRatio);
        const x = clamp(rawX, 0, Math.max(1, pageWidth - 1));
        const y = clamp(rawY, 0, Math.max(1, pageHeight - 1));
        const style = resolvePdfFontStyle(pdfPage, item.fontName, styleEntry);
        const boundedHeight = Math.min(height, Math.max(1, pageHeight - y));
        const boundedBaselineY = clamp(baselineY, y, y + boundedHeight);

        fragments.push({
            id: `frag-${pageNumber}-${index}`,
            page: pageNumber,
            text,
            x,
            y,
            width: Math.min(width, Math.max(1, pageWidth - x)),
            height: boundedHeight,
            fontSize: Math.max(8, glyphHeight),
            // Unfloored glyph height in render units: the only honest size
            // for geometry (column gaps, line advance) when a page renders
            // small — fontSize is clamped to 8px for legible replacements.
            glyphHeight: Math.max(1, trueGlyphHeight),
            fontName: String(item.fontName || ''),
            fontFamily: style.fontFamily,
            cssFontFamily: styleEntry.fontFamily ? String(styleEntry.fontFamily) : '',
            bold: style.bold,
            italic: style.italic,
            color: '#111827',
            align: 'left',
            baselineY: boundedBaselineY,
            baselineOffset: boundedBaselineY - y,
            ascentRatio,
            descentRatio,
        });
    });

    return fragments;
}

// A PDF whose fonts carry no usable Unicode map (iOS "Print > Save to PDF"
// rewrites them that way) hands back control characters and shifted codes:
// "Appellant, Pro se" arrives as "<<188-:@\x07\x02#>;\x02?1". The page looks
// right because the glyphs draw fine, but there is no real text to edit.
export function textLooksUnreadable(text) {
    const value = String(text || '').replace(/[\t\n\r]/g, '');
    if (value.length < 8) {
        return false;
    }

    const broken = (value.match(/[\u0000-\u001f\u007f-\u009f\ufffd\ue000-\uf8ff]/g) || []).length;
    return broken >= 3 && broken / value.length >= 0.05;
}

export function pageTextLooksUnreadable(pageNumber) {
    const fragments = state.pageTextFragments.get(pageNumber) || [];
    return textLooksUnreadable(fragments.map((fragment) => fragment.text).join(''));
}

export function isLikelyCheckedGlyph(character) {
    return /[☑☒✅✔✕✖✗✘■]/u.test(String(character || ''));
}

export function syncTextControlButtons() {
    textBoldEl.classList.toggle('active', !!state.textStyle.bold);
    textItalicEl.classList.toggle('active', !!state.textStyle.italic);
    textUnderlineEl.classList.toggle('active', !!state.textStyle.underline);
    textFontEl.value = state.textStyle.fontFamily;
    textAlignEl.value = state.textStyle.align;
}

export function getTextAnnotationBounds(annotation) {
    const fontSize = Math.max(10, Number(annotation.fontSize || 16));
    const lineHeight = Math.round(fontSize * 1.25);
    const lineCount = Math.max(1, String(annotation.text || '').split(/\n/).length);
    const boxWidth = Math.max(120, Number(annotation.boxWidth || 240));
    const boxHeight = Math.max(fontSize + 8, fontSize + ((lineCount - 1) * lineHeight) + 8);

    return {
        x: Number(annotation.x || 0),
        y: Number(annotation.y || 0),
        width: boxWidth,
        height: boxHeight,
        fontSize,
        lineHeight,
        lineCount,
    };
}

export function clearTextSelection() {
    setSelectedAnnotation(null, false);
}

export function findReplaceAnnotationAtPoint(pageNumber, x, y) {
    for (let index = state.annotations.length - 1; index >= 0; index--) {
        const annotation = state.annotations[index];
        if (annotation.type !== 'replace-text' || annotation.page !== pageNumber) {
            continue;
        }

        const withinX = x >= annotation.x && x <= (annotation.x + annotation.width);
        const withinY = y >= annotation.y && y <= (annotation.y + annotation.height);
        if (withinX && withinY) {
            return annotation;
        }
    }

    return null;
}

export function findTextFragmentAtPoint(pageNumber, x, y) {
    const fragments = state.pageTextFragments.get(pageNumber) || [];
    if (!fragments.length) {
        return null;
    }

    let nearest = null;
    let nearestDistance = Infinity;
    let nearestVerticalGap = Infinity;

    for (let index = fragments.length - 1; index >= 0; index--) {
        const fragment = fragments[index];
        const minX = fragment.x - 3;
        const maxX = fragment.x + fragment.width + 3;
        const minY = fragment.y - 3;
        const maxY = fragment.y + fragment.height + 3;
        const withinX = x >= minX && x <= maxX;
        const withinY = y >= minY && y <= maxY;

        if (withinX && withinY) {
            return fragment;
        }

        const dx = x < minX ? (minX - x) : (x > maxX ? (x - maxX) : 0);
        const dy = y < minY ? (minY - y) : (y > maxY ? (y - maxY) : 0);
        // Weight vertical distance more heavily so a click in the whitespace between two rows
        // doesn't snap to a fragment on the wrong line. See robustness note in the diagnosis.
        const distance = Math.hypot(dx, dy * 1.8);
        if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestVerticalGap = dy;
            nearest = fragment;
        }
    }

    // Only fall back to the nearest fragment when the cursor is genuinely close, and never when
    // it sits clearly on another line (large vertical gap).
    return nearestDistance <= 6 && nearestVerticalGap <= 4 ? nearest : null;
}

export function textFragmentsShareLine(base, candidate) {
    if (!base || !candidate) {
        return false;
    }

    const baseFontSize = Math.max(8, Number(base.fontSize || 12));
    const candidateFontSize = Math.max(8, Number(candidate.fontSize || 12));
    if (Math.abs(baseFontSize - candidateFontSize) > Math.max(2.5, baseFontSize * 0.18)) {
        return false;
    }

    const baseBaseline = Number.isFinite(Number(base.baselineY))
        ? Number(base.baselineY)
        : Number(base.y || 0) + (baseFontSize * 0.86);
    const candidateBaseline = Number.isFinite(Number(candidate.baselineY))
        ? Number(candidate.baselineY)
        : Number(candidate.y || 0) + (candidateFontSize * 0.86);

    if (Math.abs(baseBaseline - candidateBaseline) <= Math.max(2.5, baseFontSize * 0.18)) {
        return true;
    }

    const top = Math.max(Number(base.y || 0), Number(candidate.y || 0));
    const bottom = Math.min(
        Number(base.y || 0) + Number(base.height || 0),
        Number(candidate.y || 0) + Number(candidate.height || 0)
    );
    const overlap = Math.max(0, bottom - top);
    const shortestHeight = Math.max(1, Math.min(Number(base.height || 1), Number(candidate.height || 1)));

    return overlap / shortestHeight >= 0.58;
}

export function isDottedLeaderFragment(fragment) {
    const text = String(fragment && fragment.text ? fragment.text : '');
    // A run made up only of dotted-leader characters / spaces (e.g. the ". . . . ." between a
    // form label and its answer). These should never be folded into an editable run. See bug #6.
    return /^[.·•‥…\s]+$/.test(text) && /[.·•‥…]/.test(text);
}

export function getTextFragmentJoiner(previous, next, base) {
    const previousText = String(previous && previous.text ? previous.text : '');
    const nextText = String(next && next.text ? next.text : '');
    if (!previousText || !nextText || /\s$/.test(previousText) || /^\s/.test(nextText) || /^[,.;:!?)]/.test(nextText)) {
        return '';
    }

    const previousRight = Number(previous.x || 0) + Number(previous.width || 0);
    const gap = Number(next.x || 0) - previousRight;
    const baseFontSize = Math.max(8, Number(base && base.fontSize ? base.fontSize : 12));

    return gap > Math.max(1.25, baseFontSize * 0.12) ? ' ' : '';
}

export function buildReplacementFragment(pageNumber, seedFragment) {
    if (!seedFragment) {
        return seedFragment;
    }

    const fragments = (state.pageTextFragments.get(pageNumber) || [])
        .filter((fragment) => textFragmentsShareLine(seedFragment, fragment))
        .sort((a, b) => Number(a.x || 0) - Number(b.x || 0));
    const seedIndex = fragments.findIndex((fragment) => fragment.id === seedFragment.id);
    if (seedIndex < 0) {
        return seedFragment;
    }

    let start = seedIndex;
    let end = seedIndex;

    while (start > 0 && textFragmentsCanMerge(fragments[start - 1], fragments[start], seedFragment)) {
        start -= 1;
    }

    while (end < fragments.length - 1 && textFragmentsCanMerge(fragments[end], fragments[end + 1], seedFragment)) {
        end += 1;
    }

    const group = fragments.slice(start, end + 1);
    if (group.length <= 1) {
        return seedFragment;
    }

    const left = Math.min(...group.map((fragment) => Number(fragment.x || 0)));
    const top = Math.min(...group.map((fragment) => Number(fragment.y || 0)));
    const right = Math.max(...group.map((fragment) => Number(fragment.x || 0) + Number(fragment.width || 0)));
    const bottom = Math.max(...group.map((fragment) => Number(fragment.y || 0) + Number(fragment.height || 0)));
    const fontSize = Math.max(...group.map((fragment) => Math.max(8, Number(fragment.fontSize || 12))));
    const baselineY = Number.isFinite(Number(seedFragment.baselineY))
        ? Number(seedFragment.baselineY)
        : top + (fontSize * 0.86);
    let text = '';

    group.forEach((fragment, index) => {
        text += index === 0 ? String(fragment.text || '') : getTextFragmentJoiner(group[index - 1], fragment, seedFragment) + String(fragment.text || '');
    });

    // Strip stray trailing dotted-leader characters that got captured at the end of the run —
    // a run of 2+ dots, or a single dot/ellipsis that trails sentence punctuation or a space
    // (e.g. "...United States?." → "...United States?"). Conservative so abbreviations like
    // "Inc." keep their period. See bug #6.
    text = text
        .replace(/[ \t]*[.·•‥…]{2,}[ \t]*$/u, '')
        .replace(/([?!,;:])\s*[.·•‥…]\s*$/u, '$1')
        .replace(/\s+$/u, '');

    return {
        ...seedFragment,
        id: `${seedFragment.id}-run-${start}-${end}`,
        text,
        x: left,
        y: top,
        width: Math.max(1, right - left),
        height: Math.max(1, bottom - top),
        fontSize,
        color: normalizeInkColor(seedFragment.color || group.find((fragment) => fragment.color)?.color || '#111827'),
        backgroundColor: normalizeBackgroundColor(seedFragment.backgroundColor || group.find((fragment) => fragment.backgroundColor)?.backgroundColor || '#ffffff'),
        bold: group.some((fragment) => !!fragment.bold),
        italic: group.some((fragment) => !!fragment.italic),
        baselineY,
        baselineOffset: baselineY - top,
        sourceFragmentIds: group.map((fragment) => fragment.id),
    };
}

export function updateTextControlVisibility() {
    const isTextToolActive = state.activeSuiteTool === 'edit' && state.activeTool === 'text';
    textInputsEl.classList.toggle('hidden', !isTextToolActive);

    if (!isTextToolActive) {
        clearTextSelection();
    }

    syncTextControlButtons();
}

export function styleInlineEditor(editor, style) {
    editor.style.fontFamily = fontFamilyToCss(style.fontFamily);
    editor.style.fontSize = `${Math.max(10, style.fontSize)}px`;
    editor.style.fontWeight = style.bold ? '700' : '400';
    editor.style.fontStyle = style.italic ? 'italic' : 'normal';
    editor.style.textDecoration = style.underline ? 'underline' : 'none';
    editor.style.textAlign = style.align || 'left';
    editor.style.color = style.color || '#111827';
}

export function updateActiveInlineEditorStyle() {
    if (!state.activeTextEditor || !state.activeTextEditor.el) {
        return;
    }

    styleInlineEditor(state.activeTextEditor.el, getSelectedTextStyle());
}

export function commitInlineTextEditor(saveText = true) {
    if (!state.activeTextEditor || !state.activeTextEditor.el) {
        return;
    }

    const activeEditor = state.activeTextEditor;
    const editor = activeEditor.el;
    const textValue = editor.value.replace(/\r/g, '').trim();

    editor.remove();
    state.activeTextEditor = null;

    if (!saveText || textValue === '') {
        redrawPage(activeEditor.page);
        return;
    }

    const annotationId = activeEditor.annotationId || generateId();
    const existingIndex = state.annotations.findIndex((annotation) => annotation.id === annotationId);
    const annotationPayload = {
        id: annotationId,
        type: 'text',
        page: activeEditor.page,
        text: editor.value.replace(/\r/g, ''),
        x: activeEditor.x,
        y: activeEditor.y,
        fontSize: Math.max(10, getToolSize()),
        color: getToolColor(),
        fontFamily: state.textStyle.fontFamily,
        bold: state.textStyle.bold,
        italic: state.textStyle.italic,
        underline: state.textStyle.underline,
        align: state.textStyle.align,
        boxWidth: Math.max(120, Math.round(editor.offsetWidth || activeEditor.width)),
    };

    pushHistory();
    if (existingIndex >= 0) {
        state.annotations.splice(existingIndex, 1, annotationPayload);
    } else {
        state.annotations.push(annotationPayload);
    }

    state.selectedAnnotationId = annotationId;
    redrawPage(activeEditor.page);
    setStatus(existingIndex >= 0 ? 'Text updated' : 'Text added');
}

export function openInlineTextEditor(pageShell, pageNumber, point, existingAnnotation = null) {
    commitInlineTextEditor(true);

    const pageRect = getPageShellRect(pageShell);
    const existingBounds = existingAnnotation ? getTextAnnotationBounds(existingAnnotation) : null;
    const width = existingBounds
        ? existingBounds.width
        : Math.min(320, Math.max(150, pageRect.width * 0.42));
    const height = existingBounds
        ? existingBounds.height
        : Math.max(42, Math.round(getToolSize() * 2.2));
    const x = existingBounds
        ? clamp(existingBounds.x, 0, Math.max(1, pageRect.width - width))
        : clamp(point.x, 0, Math.max(1, pageRect.width - width));
    const y = existingBounds
        ? clamp(existingBounds.y, 0, Math.max(1, pageRect.height - height))
        : clamp(point.y, 0, Math.max(1, pageRect.height - height));

    if (existingAnnotation) {
        syncStyleControlsFromAnnotation(existingAnnotation);
    }

    const editor = document.createElement('textarea');
    editor.className = 'pdf-inline-text-editor';
    editor.placeholder = 'Add text here';
    editor.spellcheck = false;
    editor.style.left = `${x}px`;
    editor.style.top = `${y}px`;
    editor.style.width = `${width}px`;
    editor.style.height = `${height}px`;
    styleInlineEditor(editor, getSelectedTextStyle());
    editor.value = existingAnnotation ? String(existingAnnotation.text || '') : '';

    pageShell.appendChild(editor);

    state.activeTextEditor = {
        el: editor,
        page: pageNumber,
        x,
        y,
        width,
        height,
        annotationId: existingAnnotation ? existingAnnotation.id : null,
        committed: false,
    };

    editor.addEventListener('pointerdown', (event) => {
        event.stopPropagation();
    });

    editor.focus();
    editor.setSelectionRange(0, editor.value.length);

    editor.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            state.activeTextEditor.committed = true;
            commitInlineTextEditor(false);
            return;
        }

        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            state.activeTextEditor.committed = true;
            commitInlineTextEditor(true);
        }
    });

    editor.addEventListener('blur', () => {
        window.setTimeout(() => {
            if (!state.activeTextEditor) {
                return;
            }

            if (state.activeTextEditor.committed) {
                state.activeTextEditor = null;
                return;
            }

            commitInlineTextEditor(true);
        }, 20);
    });
}

export function commitInlineCommentEditor(saveText = true) {
    if (!state.activeCommentEditor || !state.activeCommentEditor.el) {
        return;
    }

    const activeEditor = state.activeCommentEditor;
    const editor = activeEditor.el;
    const commentText = activeEditor.textarea.value.replace(/\r/g, '').trim();
    const hadExisting = !!activeEditor.annotationId;

    editor.remove();
    state.activeCommentEditor = null;

    if (!saveText) {
        redrawPage(activeEditor.page);
        return;
    }

    if (commentText === '') {
        if (hadExisting) {
            pushHistory();
            state.annotations = state.annotations.filter((annotation) => annotation.id !== activeEditor.annotationId);
            setSelectedAnnotation(null, false);
            redrawPage(activeEditor.page);
            setStatus('Comment removed');
        } else {
            redrawPage(activeEditor.page);
        }
        return;
    }

    const annotationId = activeEditor.annotationId || generateId();
    const existingIndex = state.annotations.findIndex((annotation) => annotation.id === annotationId);
    const noteWidth = Math.max(180, Math.round(editor.offsetWidth || activeEditor.width));
    const noteHeight = Math.max(90, Math.round((activeEditor.textarea.offsetHeight || 72) + 18));

    const annotationPayload = {
        id: annotationId,
        type: 'comment',
        page: activeEditor.page,
        text: activeEditor.textarea.value.replace(/\r/g, ''),
        x: activeEditor.x,
        y: activeEditor.y,
        width: noteWidth,
        height: noteHeight,
        pinSize: Math.max(18, Number(activeEditor.pinSize || 22)),
    };

    pushHistory();
    if (existingIndex >= 0) {
        state.annotations.splice(existingIndex, 1, annotationPayload);
    } else {
        state.annotations.push(annotationPayload);
    }

    setSelectedAnnotation(annotationId, false);
    redrawPage(activeEditor.page);
    setStatus(existingIndex >= 0 ? 'Comment updated' : 'Comment added');
}

export function openInlineCommentEditor(pageShell, pageNumber, point, existingAnnotation = null) {
    commitInlineCommentEditor(true);

    const pageRect = getPageShellRect(pageShell);
    const existingBounds = existingAnnotation ? getCommentAnnotationBounds(existingAnnotation) : null;
    const pinSize = existingBounds ? existingBounds.pinSize : 22;
    const pinX = existingBounds
        ? clamp(existingBounds.x, 0, Math.max(1, pageRect.width - pinSize))
        : clamp(point.x, 0, Math.max(1, pageRect.width - pinSize));
    const pinY = existingBounds
        ? clamp(existingBounds.y, 0, Math.max(1, pageRect.height - pinSize))
        : clamp(point.y, 0, Math.max(1, pageRect.height - pinSize));

    const noteWidth = existingAnnotation
        ? Math.max(180, Number(existingAnnotation.width || 220))
        : Math.min(320, Math.max(200, pageRect.width * 0.32));
    const noteHeight = existingAnnotation
        ? Math.max(90, Number(existingAnnotation.height || 110))
        : 110;

    let editorX = pinX + pinSize + 8;
    const maxEditorX = Math.max(1, pageRect.width - noteWidth);
    if (editorX > maxEditorX) {
        editorX = Math.max(0, pinX - noteWidth - 8);
    }
    editorX = clamp(editorX, 0, maxEditorX);

    const maxEditorY = Math.max(1, pageRect.height - noteHeight);
    let editorY = clamp(pinY - 8, 0, maxEditorY);

    const wrapper = document.createElement('div');
    wrapper.className = 'pdf-inline-comment-editor';
    wrapper.style.left = `${editorX}px`;
    wrapper.style.top = `${editorY}px`;
    wrapper.style.width = `${noteWidth}px`;

    const textarea = document.createElement('textarea');
    textarea.placeholder = 'Add comment here';
    textarea.spellcheck = true;
    textarea.value = existingAnnotation ? String(existingAnnotation.text || '') : '';
    textarea.style.minHeight = `${Math.max(72, noteHeight - 38)}px`;

    const actions = document.createElement('div');
    actions.className = 'pdf-inline-comment-actions';

    const cancelButton = document.createElement('button');
    cancelButton.type = 'button';
    cancelButton.textContent = 'Cancel';
    cancelButton.dataset.role = 'cancel';

    const saveButton = document.createElement('button');
    saveButton.type = 'button';
    saveButton.textContent = existingAnnotation ? 'Update' : 'Add';
    saveButton.dataset.role = 'save';

    actions.appendChild(cancelButton);
    actions.appendChild(saveButton);
    wrapper.appendChild(textarea);
    wrapper.appendChild(actions);
    pageShell.appendChild(wrapper);

    state.activeCommentEditor = {
        el: wrapper,
        textarea,
        page: pageNumber,
        x: pinX,
        y: pinY,
        width: noteWidth,
        height: noteHeight,
        pinSize,
        annotationId: existingAnnotation ? existingAnnotation.id : null,
        committed: false,
    };

    if (existingAnnotation) {
        setSelectedAnnotation(existingAnnotation.id, false);
    } else {
        clearTextSelection();
    }

    wrapper.addEventListener('pointerdown', (event) => {
        event.stopPropagation();
    });

    saveButton.addEventListener('click', () => {
        if (!state.activeCommentEditor) {
            return;
        }
        state.activeCommentEditor.committed = true;
        commitInlineCommentEditor(true);
    });

    cancelButton.addEventListener('click', () => {
        if (!state.activeCommentEditor) {
            return;
        }
        state.activeCommentEditor.committed = true;
        commitInlineCommentEditor(false);
    });

    textarea.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            if (state.activeCommentEditor) {
                state.activeCommentEditor.committed = true;
            }
            commitInlineCommentEditor(false);
            return;
        }

        if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
            event.preventDefault();
            if (state.activeCommentEditor) {
                state.activeCommentEditor.committed = true;
            }
            commitInlineCommentEditor(true);
        }
    });

    textarea.addEventListener('blur', () => {
        window.setTimeout(() => {
            if (!state.activeCommentEditor) {
                return;
            }

            if (state.activeCommentEditor.el.contains(document.activeElement)) {
                return;
            }

            if (state.activeCommentEditor.committed) {
                state.activeCommentEditor = null;
                return;
            }

            commitInlineCommentEditor(true);
        }, 20);
    });

    textarea.focus();
    textarea.setSelectionRange(0, textarea.value.length);
}

export function getInlineReplaceFont(fragment = {}) {
    return {
        size: Math.max(8, Number(fragment.fontSize || 12)),
        family: fragment.cssFontFamily || fontFamilyToCss(fragment.fontFamily || 'Helvetica'),
        weight: fragment.bold ? '700' : '400',
        style: fragment.italic ? 'italic' : 'normal',
        color: normalizeInkColor(fragment.color || '#111827'),
    };
}

// Tight glyph-ink box (in screen/top-down coords) for a replace-text annotation or fragment.
// Uses the baseline +/- ascent/descent rather than the recorded `height` (which includes line
// leading and can be inflated by item.height), so the white mask never spills onto the line
// above or below. See bug #1 in docs/pdf-editor-edit-text-diagnosis.md.
export function getReplaceInkBox(source = {}) {
    const fontSize = Math.max(8, Number(source.fontSize || 12));
    const ascentRatio = clamp(
        Number.isFinite(Number(source.ascentRatio)) ? Number(source.ascentRatio) : 0.86,
        0.55,
        1.15
    );
    const descentRatio = clamp(
        Number.isFinite(Number(source.descentRatio)) ? Number(source.descentRatio) : 0.14,
        0.04,
        0.45
    );
    const baselineY = Number.isFinite(Number(source.baselineY))
        ? Number(source.baselineY)
        : Number(source.y || 0) + (fontSize * ascentRatio);
    const top = baselineY - (fontSize * ascentRatio);
    const bottom = baselineY + (fontSize * descentRatio);

    return {
        top,
        bottom,
        height: Math.max(1, bottom - top),
        baselineY,
        fontSize,
        ascentRatio,
        descentRatio,
    };
}

// Clamp a replace mask's vertical extent so it never paints over the ink of the line directly
// above or below it. A heading's descenders (p, y, g) can make its ink box reach into the next
// line's cap-height band; without this clamp the white mask clips the tops of that line. We keep
// the text baseline unchanged and only shrink the mask rectangle. See bug #1.
export function clampReplaceMaskToNeighbours(pageNumber, source, inkBox) {
    const fragments = state.pageTextFragments.get(pageNumber) || [];
    if (!fragments.length) {
        return { top: inkBox.top, bottom: inkBox.bottom };
    }

    const baseFontSize = Math.max(8, Number(source.fontSize || 12));
    const runBaseline = inkBox.baselineY;
    const runLeft = Number(source.x || 0);
    const runRight = runLeft + Number(source.width || 0);
    const lineSeparation = baseFontSize * 0.3;
    const sourceIds = Array.isArray(source.sourceFragmentIds) ? source.sourceFragmentIds : [];
    let belowTop = Infinity;
    let aboveBottom = -Infinity;

    fragments.forEach((other) => {
        if (!other || (source.id && other.id === source.id) || sourceIds.includes(other.id)) {
            return;
        }

        // Only neighbours that sit in the same horizontal column can clip / be clipped.
        const otherLeft = Number(other.x || 0);
        const otherRight = otherLeft + Number(other.width || 0);
        if (otherRight < runLeft || otherLeft > runRight) {
            return;
        }

        const otherInk = getReplaceInkBox(other);
        if (otherInk.baselineY > runBaseline + lineSeparation) {
            if (otherInk.top < belowTop) {
                belowTop = otherInk.top;
            }
        } else if (otherInk.baselineY < runBaseline - lineSeparation) {
            if (otherInk.bottom > aboveBottom) {
                aboveBottom = otherInk.bottom;
            }
        }
    });

    let top = inkBox.top;
    let bottom = inkBox.bottom;
    if (bottom > belowTop) {
        bottom = belowTop;
    }
    if (top < aboveBottom) {
        top = aboveBottom;
    }

    // Never collapse the mask: always cover at least the glyph body down to just past the baseline.
    const minBottom = runBaseline + Math.max(0.5, baseFontSize * 0.04);
    if (bottom < minBottom) {
        bottom = Math.min(inkBox.bottom, minBottom);
    }
    if (bottom <= top) {
        return { top: inkBox.top, bottom: inkBox.bottom };
    }

    return { top, bottom };
}

export function measureReplaceText(text, font, size) {
    const canvas = measureReplaceText.canvas
        || (measureReplaceText.canvas = document.createElement('canvas'));
    const context = canvas.getContext('2d');
    if (!context) {
        return String(text || '').length * Math.max(4, size * 0.55);
    }

    context.font = `${font.style || 'normal'} ${font.weight || '400'} ${size}px ${font.family || 'Helvetica, Arial, sans-serif'}`;
    return context.measureText(String(text || '')).width;
}

// Shrink-to-fit + ellipsis truncation shared by the on-screen preview and the exported PDF so
// "what you see is what you get". Mirrors the export draw loop's thresholds. See bug #3.
export function fitReplaceText(text, font, maxWidth, baseSize) {
    const available = Math.max(4, Number(maxWidth || 0));
    let size = Math.max(6.5, Number(baseSize || font.size || 12));
    const value = String(text || '');
    let measure = value ? measureReplaceText(value, font, size) : 0;

    while (value && measure > available && size > 6.5) {
        size -= 0.35;
        measure = measureReplaceText(value, font, size);
    }

    let output = value;
    if (value && measure > available) {
        let truncated = value;
        while (truncated.length > 1 && measureReplaceText(`${truncated}...`, font, size) > available) {
            truncated = truncated.slice(0, -1);
        }
        output = truncated.length ? `${truncated}...` : '';
        measure = output ? measureReplaceText(output, font, size) : 0;
    }

    return { text: output, size, width: measure };
}

// Right-hand limit (in screen coords) past which a replace edit must not paint or draw, so the
// mask/text cannot bleed into the neighbouring table cell. Returns the left edge of the nearest
// text to the right of the run, or Infinity when nothing blocks it. See bug #2.
export function getReplaceRightBoundary(pageNumber, fragment) {
    const fragments = state.pageTextFragments.get(pageNumber) || [];
    if (!fragments.length) {
        return Infinity;
    }

    const runIds = new Set(Array.isArray(fragment.sourceFragmentIds) ? fragment.sourceFragmentIds : []);
    if (fragment.id) {
        runIds.add(fragment.id);
    }

    const runLeft = Number(fragment.x || 0);
    const baseFontSize = Math.max(8, Number(fragment.fontSize || 12));
    // The nearest right-neighbour is found relative to the run's LEFT edge, not its right edge:
    // pdf.js often over-estimates a label's width so its box spills into the next cell, which would
    // otherwise make us discard the very neighbour we need to clamp against. Anything starting
    // clearly to the right of the run's start (and not part of the run) is a separate cell/word.
    const rightStartMin = runLeft + Math.max(2, baseFontSize * 0.3);
    // Match on baseline proximity, NOT textFragmentsShareLine: a tall heading's box vertically
    // overlaps the line below, and shareLine would then treat that lower line's left-margin text
    // as a same-line right-neighbour, collapsing the boundary onto the run's own left edge.
    const runBaseline = Number.isFinite(Number(fragment.baselineY))
        ? Number(fragment.baselineY)
        : Number(fragment.y || 0) + (baseFontSize * 0.86);
    const baselineTolerance = Math.max(2.5, baseFontSize * 0.35);
    let boundary = Infinity;

    fragments.forEach((other) => {
        if (!other || runIds.has(other.id)) {
            return;
        }

        const otherFontSize = Math.max(8, Number(other.fontSize || 12));
        const otherBaseline = Number.isFinite(Number(other.baselineY))
            ? Number(other.baselineY)
            : Number(other.y || 0) + (otherFontSize * 0.86);
        if (Math.abs(otherBaseline - runBaseline) > baselineTolerance) {
            return;
        }

        const otherLeft = Number(other.x || 0);
        if (otherLeft < rightStartMin) {
            return;
        }

        if (otherLeft < boundary) {
            boundary = otherLeft;
        }
    });

    return boundary;
}

export function resizeInlineReplaceEditorToText(activeEditor) {
    if (!activeEditor || !activeEditor.el || !activeEditor.input) {
        return;
    }

    const fragment = activeEditor.fragment || {};
    const font = activeEditor.font || getInlineReplaceFont(fragment);
    const canvas = resizeInlineReplaceEditorToText.measureCanvas
        || (resizeInlineReplaceEditorToText.measureCanvas = document.createElement('canvas'));
    const context = canvas.getContext('2d');
    const value = String(activeEditor.input.value || '');
    const textForMeasure = value.length ? value : String(fragment.text || ' ');
    let measuredWidth = 0;

    if (context) {
        context.font = `${font.style} ${font.weight} ${font.size}px ${font.family}`;
        measuredWidth = context.measureText(textForMeasure || ' ').width;
    } else {
        measuredWidth = (textForMeasure || ' ').length * Math.max(4, font.size * 0.55);
    }

    const originalWidth = Math.max(8, Number(activeEditor.originalWidth || fragment.width || 8));
    const editorX = Number(activeEditor.x || 0);
    const pageWidth = finiteNumber(activeEditor.pageWidth, editorX + originalWidth + 8);
    const rightBoundary = finiteNumber(activeEditor.rightBoundary, Infinity);
    // Clamp growth to the containing cell (next text to the right) rather than the whole page,
    // so the editor — and the committed mask width derived from it — can't overrun into the
    // neighbouring cell. See bug #2.
    const rightLimit = Math.min(pageWidth, Number.isFinite(rightBoundary) ? rightBoundary - 2 : pageWidth);
    const hardMaxWidth = Math.max(8, rightLimit - editorX - 2);
    const boundedOriginalWidth = Number.isFinite(rightBoundary)
        ? Math.min(originalWidth, hardMaxWidth)
        : originalWidth;
    const minWidth = Math.min(Math.max(28, boundedOriginalWidth + 4), hardMaxWidth);
    const maxWidth = Math.max(minWidth, hardMaxWidth);
    const targetWidth = Math.ceil(Math.max(minWidth, measuredWidth + 10));
    const nextWidth = Math.min(targetWidth, maxWidth);

    activeEditor.el.style.width = `${nextWidth}px`;
    activeEditor.width = nextWidth;
}

export function commitInlineReplaceEditor(saveText = true) {
    // Every call site that closes the legacy replace editor must also close
    // the block editor, so it is committed here as the single choke point.
    if (state.activeBlockEditor) {
        commitBlockTextEditor(saveText);
    }

    if (!state.activeReplaceEditor || !state.activeReplaceEditor.el) {
        return;
    }

    const activeEditor = state.activeReplaceEditor;
    const wrapper = activeEditor.el;
    const renderedWidth = Math.max(
        8,
        Number(activeEditor.width || 0),
        Number(wrapper.offsetWidth || 0),
        Number(activeEditor.fragment && activeEditor.fragment.width ? activeEditor.fragment.width : 0)
    );
    const renderedHeight = Math.max(
        8,
        Number(activeEditor.height || 0),
        Number(wrapper.offsetHeight || 0),
        Number(activeEditor.fragment && activeEditor.fragment.height ? activeEditor.fragment.height : 0)
    );
    const nextText = activeEditor.input.value.replace(/\r/g, '');
    const initialText = String(activeEditor.initialText ?? '').replace(/\r/g, '');
    const originalText = String(activeEditor.fragment && activeEditor.fragment.text ? activeEditor.fragment.text : '').replace(/\r/g, '');
    const hadExisting = !!activeEditor.annotationId;

    wrapper.remove();
    state.activeReplaceEditor = null;

    if (!saveText) {
        redrawPage(activeEditor.page);
        return;
    }

    if (nextText === initialText) {
        redrawPage(activeEditor.page);
        return;
    }

    if (nextText === originalText) {
        if (!hadExisting) {
            redrawPage(activeEditor.page);
            return;
        }

        pushHistory();
        state.annotations = state.annotations.filter((annotation) => annotation.id !== activeEditor.annotationId);
        redrawPage(activeEditor.page);
        setStatus('Original PDF text restored');
        return;
    }

    const annotationId = activeEditor.annotationId || generateId();
    const existingIndex = state.annotations.findIndex((annotation) => annotation.id === annotationId);
    const fragment = activeEditor.fragment;
    const rightBoundary = finiteNumber(activeEditor.rightBoundary, null);
    const boundedRenderedWidth = rightBoundary === null
        ? renderedWidth
        : Math.max(8, Math.min(renderedWidth, rightBoundary - Number(fragment.x || 0) - 2));
    const annotationPayload = {
        id: annotationId,
        type: 'replace-text',
        page: activeEditor.page,
        text: nextText,
        originalText: fragment.text || '',
        x: fragment.x,
        y: fragment.y,
        width: boundedRenderedWidth,
        height: renderedHeight,
        fontSize: Math.max(8, Number(fragment.fontSize || 12)),
        color: normalizeInkColor(fragment.color || '#111827'),
        backgroundColor: normalizeBackgroundColor(fragment.backgroundColor || '#ffffff'),
        fontFamily: fragment.fontFamily || 'Helvetica',
        cssFontFamily: fragment.cssFontFamily || '',
        bold: !!fragment.bold,
        italic: !!fragment.italic,
        align: fragment.align || 'left',
        baselineY: Number.isFinite(Number(fragment.baselineY))
            ? Number(fragment.baselineY)
            : Number(fragment.y || 0) + Math.max(8, Number(fragment.fontSize || 12)) * 0.86,
        baselineOffset: Number.isFinite(Number(fragment.baselineOffset))
            ? Number(fragment.baselineOffset)
            : Math.max(8, Number(fragment.fontSize || 12)) * 0.86,
        ascentRatio: Number.isFinite(Number(fragment.ascentRatio)) ? Number(fragment.ascentRatio) : 0.86,
        descentRatio: Number.isFinite(Number(fragment.descentRatio)) ? Number(fragment.descentRatio) : 0.14,
        rightBoundary,
        sourceFragmentIds: Array.isArray(fragment.sourceFragmentIds)
            ? fragment.sourceFragmentIds.slice()
            : (fragment.id ? [fragment.id] : []),
    };

    pushHistory();
    if (existingIndex >= 0) {
        state.annotations.splice(existingIndex, 1, annotationPayload);
    } else {
        state.annotations.push(annotationPayload);
    }

    redrawPage(activeEditor.page);
    setStatus(nextText === ''
        ? 'Text deleted'
        : (existingIndex >= 0 ? 'Text edit updated' : 'Text edited'));
}

export function openInlineReplaceEditor(pageShell, pageNumber, fragment, existingAnnotation = null) {
    commitInlineReplaceEditor(true);
    commitInlineFormFieldEditor(true);

    const pageRect = getPageShellRect(pageShell);
    const rightBoundary = getReplaceRightBoundary(pageNumber, fragment);
    const font = getInlineReplaceFont(fragment);
    const backgroundColor = normalizeBackgroundColor(fragment.backgroundColor || '#ffffff');
    const baselineOffset = Number.isFinite(Number(fragment.baselineOffset))
        ? Number(fragment.baselineOffset)
        : font.size * 0.86;
    const baselineY = Number.isFinite(Number(fragment.baselineY))
        ? Number(fragment.baselineY)
        : Number(fragment.y || 0) + baselineOffset;
    const editorHeight = Math.max(18, Math.min(48, Number(fragment.height || font.size) + 2));
    const inputLineHeight = Math.max(10, font.size * 1.05);
    const inputHeight = Math.max(inputLineHeight, font.size * 1.16);
    const preferredX = Number(fragment.x || 0);
    const preferredY = Number(fragment.y || 0);
    const rightLimit = Math.min(pageRect.width, Number.isFinite(rightBoundary) ? rightBoundary - 2 : pageRect.width);
    const maxInitialWidth = Math.max(8, rightLimit - preferredX - 2);
    const editorWidth = Math.min(maxInitialWidth, Math.max(28, Number(fragment.width || 8) + 6));
    const maxX = Math.max(1, pageRect.width - editorWidth - 2);
    const maxY = Math.max(1, pageRect.height - editorHeight - 2);
    const editorX = clamp(preferredX, 0, maxX);
    const editorY = clamp(preferredY, 0, maxY);
    const inputTop = clamp(baselineY - editorY - (font.size * 0.86), -2, Math.max(0, editorHeight - inputHeight + 2));

    const wrapper = document.createElement('div');
    wrapper.className = 'pdf-inline-replace-editor';
    wrapper.style.left = `${editorX}px`;
    wrapper.style.top = `${editorY}px`;
    wrapper.style.width = `${editorWidth}px`;
    wrapper.style.height = `${editorHeight}px`;
    wrapper.style.background = backgroundColor;

    const input = document.createElement('input');
    input.type = 'text';
    input.autocomplete = 'off';
    input.spellcheck = false;
    input.value = existingAnnotation ? String(existingAnnotation.text || '') : String(fragment.text || '');
    input.style.fontFamily = font.family;
    input.style.fontSize = `${font.size}px`;
    input.style.fontWeight = font.weight;
    input.style.fontStyle = font.style;
    input.style.color = font.color;
    input.style.background = backgroundColor;
    input.style.height = `${inputHeight}px`;
    input.style.lineHeight = `${inputLineHeight}px`;
    input.style.top = `${inputTop}px`;

    wrapper.appendChild(input);
    pageShell.appendChild(wrapper);

    state.activeReplaceEditor = {
        el: wrapper,
        input,
        page: pageNumber,
        fragment,
        annotationId: existingAnnotation ? existingAnnotation.id : null,
        initialText: input.value,
        originalWidth: Math.max(8, Number(fragment.width || 8)),
        x: editorX,
        y: editorY,
        width: editorWidth,
        height: editorHeight,
        pageWidth: pageRect.width,
        rightBoundary,
        font,
        baselineOffset,
        committed: false,
    };

    resizeInlineReplaceEditorToText(state.activeReplaceEditor);

    wrapper.addEventListener('pointerdown', (event) => {
        event.stopPropagation();
    });

    input.addEventListener('input', () => {
        if (!state.activeReplaceEditor) {
            return;
        }

        wrapper.classList.toggle('is-deletion-pending', input.value === '');
        resizeInlineReplaceEditorToText(state.activeReplaceEditor);
    });

    input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            if (state.activeReplaceEditor) {
                state.activeReplaceEditor.committed = true;
            }
            commitInlineReplaceEditor(false);
            return;
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            if (state.activeReplaceEditor) {
                state.activeReplaceEditor.committed = true;
            }
            commitInlineReplaceEditor(true);
        }
    });

    input.addEventListener('blur', () => {
        window.setTimeout(() => {
            if (!state.activeReplaceEditor) {
                return;
            }

            if (state.activeReplaceEditor.el.contains(document.activeElement)) {
                return;
            }

            if (state.activeReplaceEditor.committed) {
                state.activeReplaceEditor = null;
                return;
            }

            commitInlineReplaceEditor(true);
        }, 20);
    });

    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
}

export function getFragmentBaseline(fragment) {
    const fontSize = Math.max(8, Number(fragment.fontSize || 12));
    return Number.isFinite(Number(fragment.baselineY))
        ? Number(fragment.baselineY)
        : Number(fragment.y || 0) + (fontSize * BLOCK_DEFAULT_ASCENT);
}

export function blockRunStylesEqual(a, b) {
    return a.fontFamily === b.fontFamily
        && !!a.bold === !!b.bold
        && !!a.italic === !!b.italic
        && Math.abs(Number(a.fontSize || 0) - Number(b.fontSize || 0)) < 0.75
        && String(a.color || '') === String(b.color || '');
}

export function finalizeBlockTextLine(fragments) {
    const sorted = fragments.slice().sort((a, b) => Number(a.x || 0) - Number(b.x || 0));
    let top = Infinity;
    let bottom = -Infinity;
    let left = Infinity;
    let right = -Infinity;
    let dominant = sorted[0];

    sorted.forEach((fragment) => {
        const ink = getReplaceInkBox(fragment);
        top = Math.min(top, ink.top);
        bottom = Math.max(bottom, ink.bottom);
        left = Math.min(left, Number(fragment.x || 0));
        right = Math.max(right, Number(fragment.x || 0) + Number(fragment.width || 0));
        if (Number(fragment.width || 0) > Number(dominant.width || 0)) {
            dominant = fragment;
        }
    });

    const fontSize = Math.max(8, Number(dominant.fontSize || 12));
    const glyphHeight = Math.max(1, ...sorted.map((fragment) => Number(fragment.glyphHeight) || Number(fragment.fontSize) || fontSize));
    const runs = [];

    sorted.forEach((fragment, index) => {
        let prefix = '';
        if (index > 0) {
            const previous = sorted[index - 1];
            const gap = Number(fragment.x || 0) - (Number(previous.x || 0) + Number(previous.width || 0));
            // A space is ~0.28em of the TRUE glyph height; the floored
            // fontSize glued "year and want" on a small render.
            const spaceWidth = Math.max(0.6, (Number(dominant.glyphHeight) || fontSize) * 0.28);
            if (gap > spaceWidth * 0.45) {
                prefix = ' '.repeat(clamp(Math.round(gap / spaceWidth), 1, 60));
            } else {
                prefix = getTextFragmentJoiner(previous, fragment, dominant);
            }
        }

        const style = {
            fontFamily: fragment.fontFamily || 'Helvetica',
            bold: !!fragment.bold,
            italic: !!fragment.italic,
            fontSize: round1(Math.max(8, Number(fragment.fontSize || fontSize))),
            color: normalizeInkColor(fragment.color || '#111827'),
        };
        const text = prefix + String(fragment.text || '');
        const last = runs.length ? runs[runs.length - 1] : null;
        if (last && blockRunStylesEqual(last, style)) {
            last.text += text;
        } else {
            runs.push({ ...style, text });
        }
    });

    return {
        x: left,
        right,
        width: Math.max(1, right - left),
        top,
        bottom,
        baselineY: getFragmentBaseline(dominant),
        fontSize,
        fontFamily: dominant.fontFamily || 'Helvetica',
        color: normalizeInkColor(dominant.color || '#111827'),
        backgroundColor: normalizeBackgroundColor(dominant.backgroundColor || '#ffffff'),
        ascentRatio: Number.isFinite(Number(dominant.ascentRatio)) ? Number(dominant.ascentRatio) : BLOCK_DEFAULT_ASCENT,
        descentRatio: Number.isFinite(Number(dominant.descentRatio)) ? Number(dominant.descentRatio) : BLOCK_DEFAULT_DESCENT,
        runs,
        glyphHeight,
        text: runs.map((run) => run.text).join(''),
    };
}

/** A line-item marker at the start of a line: "14 ", "7b ", "(a) ", "iv. ", a bullet. */
const BLOCK_ITEM_MARKER = /^\s*(?:\(?\d{1,2}[a-z]?\)?[.)]?|\(?[a-z]\)|[ivx]{1,4}[.)]|[\u2022\u00b7\u25aa\u25a0\u25a1\u2610])\s+\S/i;

export function blockIntervalsOverlapRatio(a, b) {
    const overlap = Math.min(a.right, b.right) - Math.max(a.x, b.x);
    if (overlap <= 0) {
        return 0;
    }

    return overlap / Math.max(1, Math.min(a.right - a.x, b.right - b.x));
}

export function buildPageTextBlocks(pageNumber) {
    if (state.pageTextBlocks.has(pageNumber)) {
        return state.pageTextBlocks.get(pageNumber);
    }

    const lines = buildPageTextLines(pageNumber);

    // Geometry in render units: the true glyph height, never the 8px-floored
    // fontSize (which merged every row of a tax form on a small render).
    const lineEm = (line) => Math.max(1, Number(line.glyphHeight) || Number(line.fontSize) || 12);

    // Typical distance between consecutive baselines on the page, used to
    // decide how large a vertical gap can be while still belonging to the
    // same block (generous, so blank lines stay inside one editable block).
    const deltas = [];
    for (let index = 1; index < lines.length; index += 1) {
        const delta = lines[index].baselineY - lines[index - 1].baselineY;
        const em = lineEm(lines[index]);
        if (delta >= em * 0.8 && delta <= em * 5) {
            deltas.push(delta);
        }
    }
    deltas.sort((a, b) => a - b);
    const typicalAdvance = deltas.length
        ? deltas[Math.floor(deltas.length / 2)]
        : (lines.length ? lineEm(lines[0]) * 1.3 : 18);

    const drafts = [];
    lines.forEach((line) => {
        const em = lineEm(line);
        const startsItem = BLOCK_ITEM_MARKER.test(String(line.text || ''));

        // A row that starts well left of a block yet spans it (an item that
        // runs across the page, a full-width heading) ends that block: on a
        // form the right column's next cells belong to the new item even
        // though they carry no number of their own.
        drafts.forEach((draft) => {
            if (!draft.closed
                && blockIntervalsOverlapRatio(draft, line) >= 0.5
                && line.x < draft.x - em * 3) {
                draft.closed = true;
            }
        });

        let best = null;
        let bestScore = -Infinity;
        // A numbered / lettered / bulleted item always opens its own block:
        // on a form or a list "click a paragraph" means that item, not the
        // whole column.
        if (!startsItem) {
            drafts.forEach((draft) => {
                if (draft.closed) {
                    return;
                }
                const lastLine = draft.lines[draft.lines.length - 1];
                const gap = line.baselineY - lastLine.baselineY;
                if (gap < Math.max(1.5, em * 0.55)) {
                    return;
                }

                const gapLimit = Math.max(typicalAdvance * 3.4, Math.max(em, draft.em) * 4.2);
                if (gap > gapLimit) {
                    return;
                }

                const ratio = blockIntervalsOverlapRatio(draft, line);
                if (ratio < 0.5) {
                    return;
                }

                // A line starting well left of the block is a wider structure
                // (a row spanning the page, another column's item), not a
                // continuation; a first-line indent going the other way is fine.
                if (line.x < draft.x - em * 3) {
                    return;
                }

                // Among drafts that all contain the line, the nearest one
                // (vertically, then by left edge) is the paragraph it belongs
                // to — not the oldest wide block that happens to span it.
                const score = ratio
                    - (gap / gapLimit) * 0.25
                    - (Math.abs(line.x - draft.x) / (em * 12)) * 0.1;
                if (score > bestScore) {
                    bestScore = score;
                    best = draft;
                }
            });
        }

        if (best) {
            best.lines.push(line);
            best.x = Math.min(best.x, line.x);
            best.right = Math.max(best.right, line.right);
            best.fontSize = Math.max(best.fontSize, line.fontSize);
            best.em = Math.max(best.em, em);
        } else {
            drafts.push({
                x: line.x,
                right: line.right,
                fontSize: line.fontSize,
                em,
                lines: [line],
            });
        }
    });

    const blocks = drafts.map((draft, index) => {
        const blockLines = draft.lines;
        const top = Math.min(...blockLines.map((line) => line.top));
        const bottom = Math.max(...blockLines.map((line) => line.bottom));

        const advances = [];
        for (let lineIndex = 1; lineIndex < blockLines.length; lineIndex += 1) {
            advances.push(blockLines[lineIndex].baselineY - blockLines[lineIndex - 1].baselineY);
        }
        advances.sort((a, b) => a - b);

        // Dominant width-weighted style for the block.
        let dominant = blockLines[0];
        blockLines.forEach((line) => {
            if (line.width > dominant.width) {
                dominant = line;
            }
        });

        const fontSize = Math.max(8, dominant.fontSize);
        // The block's line unit is the SMALLEST plausible advance (robust
        // 10th percentile), not the median: on grid layouts (pleading
        // paper, double-spaced captions) larger advances are multiples of
        // this unit and become explicit blank lines, so original spacing
        // survives round-trips exactly.
        const plausible = advances.filter((advance) => advance >= fontSize * 1.02 && advance <= fontSize * 3.4);
        const rawLineHeight = plausible.length
            ? plausible[Math.floor((plausible.length - 1) * 0.1)]
            : (advances.length ? advances[Math.floor(advances.length / 2)] : fontSize * 1.4);
        const lineHeight = clamp(rawLineHeight, fontSize * 1.02, fontSize * 3.4);

        return {
            id: `block-${pageNumber}-${index}`,
            page: pageNumber,
            x: draft.x,
            right: draft.right,
            top,
            bottom,
            width: Math.max(1, draft.right - draft.x),
            height: Math.max(1, bottom - top),
            fontSize,
            fontFamily: dominant.fontFamily,
            color: dominant.color,
            backgroundColor: dominant.backgroundColor,
            ascentRatio: dominant.ascentRatio,
            descentRatio: dominant.descentRatio,
            lineHeight,
            firstBaselineOffset: blockLines[0].baselineY - top,
            lines: blockLines,
        };
    });

    state.pageTextBlocks.set(pageNumber, blocks);
    return blocks;
}

export function findTextBlockAtPoint(pageNumber, x, y) {
    const blocks = buildPageTextBlocks(pageNumber);
    let hit = null;
    blocks.forEach((block) => {
        const withinX = x >= block.x - 4 && x <= block.right + 4;
        const withinY = y >= block.top - 4 && y <= block.bottom + 4;
        if (!withinX || !withinY) {
            return;
        }

        if (!hit || (block.width * block.height) < (hit.width * hit.height)) {
            hit = block;
        }
    });

    return hit;
}

export function findTextBlockById(pageNumber, blockId) {
    if (!blockId) {
        return null;
    }

    return buildPageTextBlocks(pageNumber).find((block) => block.id === blockId) || null;
}

export function detectBlockLineAlign(block, line) {
    const blockCenter = block.x + (block.width / 2);
    const lineCenter = line.x + (line.width / 2);
    const centered = Math.abs(lineCenter - blockCenter) <= Math.max(6, block.width * 0.02)
        && line.width < block.width * 0.92
        && (line.x - block.x) > block.width * 0.04;
    return centered ? 'center' : 'left';
}

// Editable-line representation: explicit blank lines for vertical gaps so
// the user can simply delete them to remove blank space.
export function getBlockEditorLines(block) {
    const out = [];
    let previousBaseline = null;

    block.lines.forEach((line) => {
        let advanceExtra = 0;
        if (previousBaseline !== null) {
            const advance = line.baselineY - previousBaseline;
            const fillers = Math.max(0, Math.round(advance / block.lineHeight) - 1);
            for (let index = 0; index < fillers; index += 1) {
                out.push({ advanceExtra: 0, indent: 0, align: 'left', runs: [] });
            }
            // May be slightly negative when the original advance is a bit
            // under the line unit; preserving it keeps baselines exact.
            advanceExtra = clamp(round1(advance - ((fillers + 1) * block.lineHeight)), -block.lineHeight * 0.45, block.lineHeight);
        }

        out.push({
            advanceExtra,
            indent: round1(Math.max(0, line.x - block.x)),
            align: detectBlockLineAlign(block, line),
            runs: line.runs.map((run) => ({ ...run })),
        });
        previousBaseline = line.baselineY;
    });

    return out;
}

export function blockLinesSignature(lines) {
    return JSON.stringify((lines || []).map((line) => ({
        a: Math.round(Number(line.advanceExtra || 0) / 2),
        i: Math.round(Number(line.indent || 0) / 2),
        g: line.align === 'center' ? 'c' : 'l',
        r: (line.runs || []).map((run) => ({
            t: String(run.text || ''),
            f: run.fontFamily || 'Helvetica',
            b: !!run.bold,
            e: !!run.italic,
            s: Math.round(Number(run.fontSize || 0) * 2) / 2,
            c: normalizeInkColor(run.color || '#111827'),
        })),
    })));
}

export function blockEditorLineHtml(line) {
    const styleBits = [];
    if (line.align === 'center') {
        styleBits.push('text-align:center');
    } else if (Number(line.indent || 0) > 0.5) {
        styleBits.push(`padding-left:${round1(line.indent)}px`);
    }
    if (Math.abs(Number(line.advanceExtra || 0)) > 0.5) {
        styleBits.push(`margin-top:${round1(line.advanceExtra)}px`);
    }

    const runsHtml = (line.runs || [])
        .filter((run) => String(run.text || '') !== '')
        .map((run) => {
            const family = fontFamilyToCss(run.fontFamily || 'Helvetica');
            const weight = run.bold ? '700' : '400';
            const fontStyle = run.italic ? 'italic' : 'normal';
            const size = Math.max(8, Number(run.fontSize || 12));
            const color = normalizeInkColor(run.color || '#111827');
            return `<span style="font-family:${escapeSvg(family)};font-size:${size}px;font-weight:${weight};font-style:${fontStyle};color:${color}">${escapeSvg(run.text)}</span>`;
        })
        .join('');

    const styleAttr = styleBits.length ? ` style="${styleBits.join(';')}"` : '';
    return `<div class="pdf-bte-line"${styleAttr}>${runsHtml || '<br>'}</div>`;
}

export function mapCssFontFamilyToStandard(cssFamily) {
    const normalized = String(cssFamily || '').toLowerCase();
    if (normalized.includes('courier') || normalized.includes('mono')) {
        return 'Courier';
    }

    if (normalized.includes('times') || normalized.includes('georgia') || (normalized.includes('serif') && !normalized.includes('sans'))) {
        return 'TimesRoman';
    }

    return 'Helvetica';
}

export function resolveBlockNodeRunStyle(node, defaults) {
    const parent = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
    if (!parent || !(parent instanceof Element)) {
        return { ...defaults };
    }

    let computed;
    try {
        computed = window.getComputedStyle(parent);
    } catch (error) {
        return { ...defaults };
    }

    const weight = parseInt(computed.fontWeight, 10);
    return {
        fontFamily: mapCssFontFamilyToStandard(computed.fontFamily || ''),
        bold: Number.isFinite(weight) ? weight >= 600 : /bold/i.test(computed.fontWeight || ''),
        italic: /italic|oblique/i.test(computed.fontStyle || ''),
        fontSize: round1(parseFloat(computed.fontSize) || defaults.fontSize),
        color: normalizeInkColor(cssColorToHex(computed.color, defaults.color)),
    };
}

// Parse one line element into 1..n editor lines (inner <br> splits a line).
export function parseBlockEditorLineElement(el, defaults) {
    const baseLine = () => ({
        advanceExtra: round1(parseFloat(el.style ? el.style.marginTop : 0) || 0),
        indent: round1(parseFloat(el.style ? el.style.paddingLeft : 0) || 0),
        align: el.style && el.style.textAlign === 'center' ? 'center' : 'left',
        runs: [],
    });

    const lines = [baseLine()];

    const appendRun = (text, style) => {
        if (text === '') {
            return;
        }

        const line = lines[lines.length - 1];
        const last = line.runs.length ? line.runs[line.runs.length - 1] : null;
        if (last && blockRunStylesEqual(last, style)) {
            last.text += text;
        } else {
            line.runs.push({ ...style, text });
        }
    };

    const walk = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            const text = String(node.nodeValue || '').replace(/\u00A0/g, ' ').replace(/[\r\n]/g, '');
            appendRun(text, resolveBlockNodeRunStyle(node, defaults));
            return;
        }

        if (node.nodeType !== Node.ELEMENT_NODE) {
            return;
        }

        if (node.tagName === 'BR') {
            // Only a <br> with following content splits the line; a lone
            // trailing <br> is how browsers represent an empty line box.
            lines.push({ ...baseLine(), runs: [] });
            return;
        }

        Array.from(node.childNodes).forEach(walk);
    };

    Array.from(el.childNodes).forEach(walk);

    // A trailing empty line created by a terminal <br> collapses back.
    if (lines.length > 1 && !lines[lines.length - 1].runs.length) {
        lines.pop();
    }

    return lines;
}

export function parseBlockEditorLines(editorEl, defaults) {
    const lines = [];
    let strayNodes = [];

    const flushStray = () => {
        if (!strayNodes.length) {
            return;
        }

        const holder = document.createElement('div');
        strayNodes.forEach((node) => holder.appendChild(node.cloneNode(true)));
        // The clone is detached, so computed styles fall back to defaults —
        // acceptable for the rare stray inline content contenteditable leaves
        // outside a line div.
        const runs = [];
        const text = holder.textContent.replace(/\u00A0/g, ' ').replace(/[\r\n]/g, '');
        if (text !== '') {
            runs.push({ ...defaults, text });
        }
        lines.push({ advanceExtra: 0, indent: 0, align: 'left', runs });
        strayNodes = [];
    };

    Array.from(editorEl.childNodes).forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE && (node.tagName === 'DIV' || node.tagName === 'P')) {
            flushStray();
            parseBlockEditorLineElement(node, defaults).forEach((line) => lines.push(line));
            return;
        }

        if (node.nodeType === Node.TEXT_NODE && String(node.nodeValue || '').trim() === '') {
            return;
        }

        strayNodes.push(node);
    });
    flushStray();

    // Trailing blank lines add nothing — drop them so the mask stays tight.
    while (lines.length && !lines[lines.length - 1].runs.length) {
        lines.pop();
    }

    return lines;
}

export function getActiveBlockGeometry(block, annotation) {
    if (annotation) {
        return {
            blockId: annotation.sourceBlockId || (block ? block.id : null),
            x: Number(annotation.x || 0),
            top: Number(annotation.y || 0),
            width: Math.max(8, Number(annotation.width || 8)),
            height: Math.max(8, Number(annotation.height || 8)),
            lineHeight: Math.max(6, Number(annotation.lineHeight || 16)),
            fontSize: Math.max(8, Number(annotation.fontSize || 12)),
            fontFamily: annotation.fontFamily || 'Helvetica',
            color: normalizeInkColor(annotation.color || '#111827'),
            backgroundColor: normalizeBackgroundColor(annotation.backgroundColor || '#ffffff'),
            firstBaselineOffset: Number(annotation.firstBaselineOffset || Math.max(8, Number(annotation.fontSize || 12)) * BLOCK_DEFAULT_ASCENT),
            ascentRatio: Number(annotation.ascentRatio || BLOCK_DEFAULT_ASCENT),
            descentRatio: Number(annotation.descentRatio || BLOCK_DEFAULT_DESCENT),
        };
    }

    return {
        blockId: block.id,
        x: block.x,
        top: block.top,
        width: block.width,
        height: block.height,
        lineHeight: block.lineHeight,
        fontSize: block.fontSize,
        fontFamily: block.fontFamily,
        color: block.color,
        backgroundColor: block.backgroundColor,
        firstBaselineOffset: block.firstBaselineOffset,
        ascentRatio: block.ascentRatio,
        descentRatio: block.descentRatio,
    };
}

export function positionBlockEditorActions(actionsEl, pageShell, editorLeft, editorTop) {
    const chipHeight = 30;
    let top = editorTop - chipHeight - 8;
    if (top < 2) {
        top = editorTop + 6;
    }

    actionsEl.style.left = `${Math.max(2, editorLeft)}px`;
    actionsEl.style.top = `${top}px`;
}

export function placeBlockEditorCaret(editorEl, clientX, clientY) {
    try {
        let range = null;
        if (document.caretRangeFromPoint) {
            range = document.caretRangeFromPoint(clientX, clientY);
        } else if (document.caretPositionFromPoint) {
            const position = document.caretPositionFromPoint(clientX, clientY);
            if (position) {
                range = document.createRange();
                range.setStart(position.offsetNode, position.offset);
                range.collapse(true);
            }
        }

        if (range && editorEl.contains(range.startContainer)) {
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(range);
        }
    } catch (error) {
        // Caret placement is best-effort.
    }
}

export function openBlockTextEditor(pageShell, pageNumber, block, existingAnnotation = null, caretEvent = null) {
    commitInlineReplaceEditor(true);
    commitInlineTextEditor(true);
    commitInlineFormFieldEditor(true);
    commitBlockTextEditor(true);

    if (!block && !existingAnnotation) {
        return;
    }

    const layout = state.pageLayouts.get(pageNumber);
    if (!layout) {
        return;
    }

    const geometry = getActiveBlockGeometry(block, existingAnnotation);
    const lines = existingAnnotation
        ? (existingAnnotation.lines || []).map((line) => ({
            ...line,
            runs: (line.runs || []).map((run) => ({ ...run })),
        }))
        : getBlockEditorLines(block);

    const padX = 3;
    const editorLeft = Math.max(0, geometry.x - padX);
    const halfLeading = Math.max(0, (geometry.lineHeight - geometry.fontSize * (geometry.ascentRatio + geometry.descentRatio)) / 2);
    const editorTop = Math.max(0, geometry.top - halfLeading - 1);
    const editorWidth = Math.max(40, Math.min(geometry.width + (padX * 2) + 8, layout.screenWidth - editorLeft - 2));

    const editor = document.createElement('div');
    editor.className = 'pdf-block-text-editor';
    editor.contentEditable = 'true';
    editor.spellcheck = false;
    editor.style.left = `${editorLeft}px`;
    editor.style.top = `${editorTop}px`;
    editor.style.minWidth = `${editorWidth}px`;
    editor.style.minHeight = `${Math.max(geometry.lineHeight, geometry.height + halfLeading + 2)}px`;
    editor.style.paddingLeft = `${padX}px`;
    editor.style.paddingRight = `${padX}px`;
    editor.style.background = geometry.backgroundColor;
    editor.style.lineHeight = `${geometry.lineHeight}px`;
    editor.style.fontFamily = fontFamilyToCss(geometry.fontFamily);
    editor.style.fontSize = `${geometry.fontSize}px`;
    editor.style.color = geometry.color;
    editor.innerHTML = lines.map(blockEditorLineHtml).join('') || '<div class="pdf-bte-line"><br></div>';

    const actions = document.createElement('div');
    actions.className = 'pdf-bte-actions';
    actions.innerHTML = `
        <span class="pdf-bte-hint">Edit like a document &mdash; delete lines and blank space to reflow</span>
        <button type="button" class="pdf-bte-cancel">Cancel</button>
        <button type="button" class="pdf-bte-done">Done</button>
    `;

    pageShell.appendChild(editor);
    pageShell.appendChild(actions);
    positionBlockEditorActions(actions, pageShell, editorLeft, editorTop);

    const defaults = {
        fontFamily: geometry.fontFamily,
        bold: false,
        italic: false,
        fontSize: round1(geometry.fontSize),
        color: geometry.color,
    };

    const originalLines = block ? getBlockEditorLines(block) : null;

    state.activeBlockEditor = {
        el: editor,
        actionsEl: actions,
        page: pageNumber,
        geometry,
        defaults,
        annotationId: existingAnnotation ? existingAnnotation.id : null,
        initialSignature: blockLinesSignature(lines),
        originalSignature: originalLines ? blockLinesSignature(originalLines) : null,
        committed: false,
    };

    // Hide the annotation being edited so its preview doesn't show through.
    redrawPage(pageNumber);

    editor.addEventListener('pointerdown', (event) => {
        event.stopPropagation();
    });
    actions.addEventListener('pointerdown', (event) => {
        event.stopPropagation();
        event.preventDefault();
    });

    actions.querySelector('.pdf-bte-done').addEventListener('click', () => {
        if (state.activeBlockEditor) {
            state.activeBlockEditor.committed = true;
        }
        commitBlockTextEditor(true);
    });
    actions.querySelector('.pdf-bte-cancel').addEventListener('click', () => {
        if (state.activeBlockEditor) {
            state.activeBlockEditor.committed = true;
        }
        commitBlockTextEditor(false);
    });

    editor.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            if (state.activeBlockEditor) {
                state.activeBlockEditor.committed = true;
            }
            commitBlockTextEditor(false);
            return;
        }

        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            if (state.activeBlockEditor) {
                state.activeBlockEditor.committed = true;
            }
            commitBlockTextEditor(true);
        }
    });

    editor.addEventListener('blur', () => {
        window.setTimeout(() => {
            const active = state.activeBlockEditor;
            if (!active || active.el !== editor) {
                return;
            }

            if (active.committed) {
                return;
            }

            const focused = document.activeElement;
            if (focused && (active.el.contains(focused) || (active.actionsEl && active.actionsEl.contains(focused)))) {
                return;
            }

            commitBlockTextEditor(true);
        }, 30);
    });

    editor.focus();
    if (caretEvent && Number.isFinite(caretEvent.clientX)) {
        placeBlockEditorCaret(editor, caretEvent.clientX, caretEvent.clientY);
    }
}

export function removeSupersededReplaceAnnotations(pageNumber, rect) {
    state.annotations = state.annotations.filter((annotation) => {
        if (annotation.type !== 'replace-text' || annotation.page !== pageNumber) {
            return true;
        }

        const centerX = Number(annotation.x || 0) + (Number(annotation.width || 0) / 2);
        const centerY = Number(annotation.y || 0) + (Number(annotation.height || 0) / 2);
        const inside = centerX >= rect.x && centerX <= rect.x + rect.width
            && centerY >= rect.y && centerY <= rect.y + rect.height;
        return !inside;
    });
}

export function commitBlockTextEditor(saveText = true) {
    const active = state.activeBlockEditor;
    if (!active || !active.el) {
        return;
    }

    const lines = parseBlockEditorLines(active.el, active.defaults);
    active.el.remove();
    if (active.actionsEl) {
        active.actionsEl.remove();
    }
    state.activeBlockEditor = null;

    if (!saveText) {
        redrawPage(active.page);
        setStatus('Edit cancelled');
        return;
    }

    const signature = blockLinesSignature(lines);
    if (signature === active.initialSignature) {
        redrawPage(active.page);
        return;
    }

    if (active.originalSignature && signature === active.originalSignature) {
        if (active.annotationId) {
            pushHistory();
            state.annotations = state.annotations.filter((annotation) => annotation.id !== active.annotationId);
            redrawPage(active.page);
            setStatus('Original PDF text restored');
            return;
        }

        redrawPage(active.page);
        return;
    }

    const geometry = active.geometry;
    const annotationId = active.annotationId || generateId();
    const payload = {
        id: annotationId,
        type: 'edit-block',
        page: active.page,
        x: geometry.x,
        y: geometry.top,
        width: geometry.width,
        height: geometry.height,
        lineHeight: geometry.lineHeight,
        fontSize: geometry.fontSize,
        fontFamily: geometry.fontFamily,
        color: geometry.color,
        backgroundColor: geometry.backgroundColor,
        firstBaselineOffset: geometry.firstBaselineOffset,
        ascentRatio: geometry.ascentRatio,
        descentRatio: geometry.descentRatio,
        sourceBlockId: geometry.blockId,
        lines,
    };

    pushHistory();
    removeSupersededReplaceAnnotations(active.page, {
        x: geometry.x,
        y: geometry.top,
        width: geometry.width,
        height: geometry.height,
    });

    const existingIndex = state.annotations.findIndex((annotation) => annotation.id === annotationId);
    if (existingIndex >= 0) {
        state.annotations.splice(existingIndex, 1, payload);
    } else {
        state.annotations.push(payload);
    }

    redrawPage(active.page);
    const isBlank = !lines.length || lines.every((line) => !line.runs.length);
    setStatus(isBlank ? 'Text deleted' : 'Text updated');
}

// Shared line layout used by the SVG preview and the PDF export so both
// place lines at identical baselines/x positions. `measureRun(run)` returns
// the run's width in screen units with the caller's font metrics.
export function layoutEditBlockLines(annotation, measureRun) {
    const lines = annotation.lines || [];
    const lineHeight = Math.max(6, Number(annotation.lineHeight || 16));
    const blockX = Number(annotation.x || 0);
    const blockWidth = Math.max(1, Number(annotation.width || 1));
    let baseline = Number(annotation.y || 0) + Number(annotation.firstBaselineOffset || 0);
    const placed = [];

    lines.forEach((line, index) => {
        if (index > 0) {
            baseline += lineHeight;
        }
        baseline += clamp(Number(line.advanceExtra || 0), -lineHeight * 0.45, lineHeight * 2);

        const runs = (line.runs || []).filter((run) => String(run.text || '') !== '');
        if (!runs.length) {
            placed.push({ line, baselineY: baseline, runs: [] });
            return;
        }

        const widths = runs.map((run) => Math.max(0, Number(measureRun(run) || 0)));
        const totalWidth = widths.reduce((sum, width) => sum + width, 0);
        let x = line.align === 'center'
            ? blockX + Math.max(0, (blockWidth - totalWidth) / 2)
            : blockX + Math.max(0, Number(line.indent || 0));

        const placedRuns = runs.map((run, runIndex) => {
            const start = x;
            x += widths[runIndex];
            return { run, x: start, width: widths[runIndex] };
        });

        placed.push({ line, baselineY: baseline, runs: placedRuns });
    });

    return placed;
}

export function getEditBlockBounds(annotation) {
    const fontSize = Math.max(8, Number(annotation.fontSize || 12));
    const descent = Number(annotation.descentRatio || BLOCK_DEFAULT_DESCENT);
    const placed = layoutEditBlockLines(annotation, () => 0);
    const lastBaseline = placed.length ? placed[placed.length - 1].baselineY : Number(annotation.y || 0);
    const contentBottom = lastBaseline + (fontSize * descent) + 1;
    const top = Number(annotation.y || 0);
    const bottom = Math.max(top + Number(annotation.height || 0), contentBottom);

    return {
        x: Number(annotation.x || 0),
        y: top,
        width: Math.max(1, Number(annotation.width || 1)),
        height: Math.max(1, bottom - top),
    };
}

export function findEditBlockAnnotationAtPoint(pageNumber, x, y) {
    for (let index = state.annotations.length - 1; index >= 0; index -= 1) {
        const annotation = state.annotations[index];
        if (annotation.type !== 'edit-block' || annotation.page !== pageNumber) {
            continue;
        }

        const bounds = getEditBlockBounds(annotation);
        const withinX = x >= bounds.x - 3 && x <= bounds.x + bounds.width + 3;
        const withinY = y >= bounds.y - 3 && y <= bounds.y + bounds.height + 3;
        if (withinX && withinY) {
            return annotation;
        }
    }

    return null;
}

export function getEditBlockSvg(annotation) {
    if (
        state.activeBlockEditor
        && state.activeBlockEditor.annotationId
        && state.activeBlockEditor.annotationId === annotation.id
    ) {
        return '';
    }

    const placed = layoutEditBlockLines(annotation, (run) => {
        const font = {
            family: fontFamilyToCss(run.fontFamily || 'Helvetica'),
            weight: run.bold ? '700' : '400',
            style: run.italic ? 'italic' : 'normal',
        };
        return measureReplaceText(run.text, font, Math.max(8, Number(run.fontSize || 12)));
    });

    const bounds = getEditBlockBounds(annotation);
    const fillColor = normalizeBackgroundColor(annotation.backgroundColor || '#ffffff');
    const maskX = Math.max(0, bounds.x - 2);
    const maskY = Math.max(0, bounds.y - 1.5);
    const maskWidth = bounds.width + 4;
    const maskHeight = bounds.height + 3;

    const textParts = [];
    placed.forEach((entry) => {
        entry.runs.forEach(({ run, x }) => {
            const family = escapeSvg(fontFamilyToCss(run.fontFamily || 'Helvetica'));
            const size = Math.max(8, Number(run.fontSize || 12));
            const color = normalizeInkColor(run.color || '#111827');
            textParts.push(`<text x="${x}" y="${entry.baselineY}" fill="${color}" font-size="${size}" font-family="${family}" font-weight="${run.bold ? '700' : '400'}" font-style="${run.italic ? 'italic' : 'normal'}" text-anchor="start">${escapeSvg(run.text)}</text>`);
        });
    });

    const isSelected = annotation.id === state.selectedAnnotationId
        && state.activeSuiteTool === 'edit'
        && (state.activeTool === 'pointer' || state.activeTool === 'replace');
    const selectionBox = isSelected
        ? `<rect x="${maskX}" y="${maskY}" width="${maskWidth}" height="${maskHeight}" fill="none" stroke="#3b82f6" stroke-width="1.2" stroke-dasharray="4 3"></rect>`
        : '';

    return `
        <g>
            <rect x="${maskX}" y="${maskY}" width="${maskWidth}" height="${maskHeight}" fill="${fillColor}" stroke="none"></rect>
            ${textParts.join('')}
            ${selectionBox}
        </g>
    `;
}
